import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Checkbox,
  Typography,
  Box,
  Chip,
  TextField,
  CircularProgress,
  Alert
} from '@mui/material';
import { format } from 'date-fns';
import { Button } from '../../design-system/components/core';
import { Card } from '../../design-system';
import EntityStatusBadge from '../core/EntityStatusBadge';
import useExpenseStore from '../../stores/expenseStore';

const ExpenseSelector = ({ open, onClose, onSelect, clientId, existingExpenseIds = [] }) => {
  const [selectedExpenses, setSelectedExpenses] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const { expenses, isLoading, fetchExpenses } = useExpenseStore();

  useEffect(() => {
    if (open) {
      // Fetch billable expenses for the client that are approved but not yet invoiced
      fetchExpenses({
        clientId,
        billable: true,
        status: 'approved',
        billingStatus: 'pending'
      });
    }
  }, [open, clientId, fetchExpenses]);

  const filteredExpenses = expenses.filter(expense => {
    // Filter out already selected expenses
    if (existingExpenseIds.includes(expense._id)) return false;
    
    // Filter by search term
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      return (
        expense.expenseNumber.toLowerCase().includes(searchLower) ||
        expense.description.toLowerCase().includes(searchLower) ||
        expense.vendor.toLowerCase().includes(searchLower) ||
        expense.category.toLowerCase().includes(searchLower)
      );
    }
    return true;
  });

  const handleToggle = (expense) => {
    const currentIndex = selectedExpenses.findIndex(e => e._id === expense._id);
    const newChecked = [...selectedExpenses];

    if (currentIndex === -1) {
      newChecked.push(expense);
    } else {
      newChecked.splice(currentIndex, 1);
    }

    setSelectedExpenses(newChecked);
  };

  const handleConfirm = () => {
    // Convert selected expenses to invoice items
    const invoiceItems = selectedExpenses.map(expense => ({
      description: `${expense.description} - ${expense.vendor} (${format(new Date(expense.date), 'MMM dd, yyyy')})`,
      quantity: 1,
      unitPrice: expense.billingAmount || expense.amount,
      amount: expense.billingAmount || expense.amount,
      itemType: 'expense',
      reference: {
        type: 'expense',
        id: expense._id
      },
      taxRate: expense.taxRate || 0,
      taxAmount: expense.taxAmount || 0
    }));

    onSelect(invoiceItems, selectedExpenses.map(e => e._id));
    setSelectedExpenses([]);
    onClose();
  };

  const handleCancel = () => {
    setSelectedExpenses([]);
    onClose();
  };

  const totalAmount = selectedExpenses.reduce((sum, expense) => 
    sum + (expense.billingAmount || expense.amount), 0
  );

  return (
    <Dialog open={open} onClose={handleCancel} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Add Billable Expenses to Invoice</Typography>
          {selectedExpenses.length > 0 && (
            <Chip
              label={`${selectedExpenses.length} selected - $${totalAmount.toFixed(2)}`}
              color="primary"
              size="small"
            />
          )}
        </Box>
      </DialogTitle>
      
      <DialogContent>
        <TextField
          fullWidth
          placeholder="Search expenses..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ mb: 2 }}
          size="small"
        />

        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : filteredExpenses.length === 0 ? (
          <Box sx={{ py: 4, textAlign: 'center' }}>
            <Typography color="textSecondary">
              No billable expenses available to add
            </Typography>
          </Box>
        ) : (
          <List sx={{ maxHeight: 400, overflow: 'auto' }}>
            {filteredExpenses.map((expense) => {
              const isSelected = selectedExpenses.some(e => e._id === expense._id);
              
              return (
                <ListItem
                  key={expense._id}
                  button
                  onClick={() => handleToggle(expense)}
                  dense
                  sx={{
                    borderRadius: 1,
                    mb: 1,
                    backgroundColor: isSelected ? 'action.selected' : 'transparent',
                    border: 1,
                    borderColor: isSelected ? 'primary.main' : 'divider',
                    '&:hover': {
                      backgroundColor: 'action.hover'
                    }
                  }}
                >
                  <ListItemIcon>
                    <Checkbox
                      edge="start"
                      checked={isSelected}
                      tabIndex={-1}
                      disableRipple
                    />
                  </ListItemIcon>
                  
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" fontWeight={500}>
                          {expense.expenseNumber}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          - {expense.vendor}
                        </Typography>
                        <Chip
                          label={expense.category.replace('_', ' ')}
                          size="small"
                          variant="outlined"
                        />
                      </Box>
                    }
                    secondary={
                      <Box sx={{ mt: 0.5 }}>
                        <Typography variant="body2" color="textSecondary">
                          {expense.description}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 0.5 }}>
                          <Typography variant="caption" color="textSecondary">
                            {format(new Date(expense.date), 'MMM dd, yyyy')}
                          </Typography>
                          <Typography variant="caption" fontWeight={500}>
                            ${(expense.billingAmount || expense.amount).toFixed(2)}
                            {expense.markupPercentage > 0 && (
                              <span style={{ color: 'green' }}>
                                {' '}(+{expense.markupPercentage}% markup)
                              </span>
                            )}
                          </Typography>
                        </Box>
                      </Box>
                    }
                  />
                </ListItem>
              );
            })}
          </List>
        )}

        {selectedExpenses.length > 0 && (
          <Alert severity="info" sx={{ mt: 2 }}>
            Selected expenses will be marked as invoiced and cannot be added to other invoices.
          </Alert>
        )}
      </DialogContent>
      
      <DialogActions>
        <Button onClick={handleCancel}>
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          disabled={selectedExpenses.length === 0}
        >
          Add {selectedExpenses.length} Expense{selectedExpenses.length !== 1 ? 's' : ''} to Invoice
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ExpenseSelector;